/**
 * Minimal JSON fetch helper.
 *
 * Replaces axios 0.19. Everything the site used it for — a GET with query
 * parameters and a JSON POST with one custom header — is native now, so the
 * dependency bought nothing.
 */

export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) {
    return path;
  }

  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    // The 2.2 service sent `from_list: null` and `to_list: null` on every
    // company search, which serialised as empty parameters for no reason.
    if (value !== undefined && value !== null) {
      search.append(key, String(value));
    }
  }

  const query = search.toString();

  return query ? `${path}?${query}` : path;
}

export async function getJson<T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(buildUrl(path, params), {
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new HttpError(`GET ${path} failed`, response.status);
  }

  return (await response.json()) as T;
}

export async function postJson<T>(
  path: string,
  body: unknown,
  headers: Record<string, string> = {},
): Promise<T | null> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...headers },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new HttpError(`POST ${path} failed`, response.status);
  }

  if (response.status === 204) {
    return null;
  }

  const text = await response.text();

  return text ? (JSON.parse(text) as T) : null;
}
